// =============================================================================
// Visual Language — Veta Virtual Dashboard Color System
// =============================================================================
//
// Two color families: Reds (brand/warm) and Blues (professional/cool)
// Gradients always stay within the same family (never red→blue)
//
// Usage:
//   Red family  → primary actions, highlights, active states, chart series 1
//   Blue family → informational, secondary data, headers, chart series 2
//   Status      → functional only (success/error/warning/info)
// =============================================================================

export const colors = {
  // Red family — brand identity (warm, bold)
  brand: {
    primary: '#e8364b', // Veta Red — hero accent, active states, chart primary
    secondary: '#f06070', // Coral — softer red for gradients, hover states
    tertiary: '#c72e40', // Crimson — deeper red for emphasis, pressed states
    light: '#fef5f6', // Blush — subtle red backgrounds
    dark: '#9a1e30', // Maroon — dark red for high-contrast text
  },
  // Blue family — professional trust (cool, calm)
  ui: {
    deepBlue: '#1e3a5f', // Navy — headers, text emphasis, chart series 2
    mediumBlue: '#2d5f8a', // Steel — secondary data, borders
    lightBlue: '#3a7ca5', // Sky — gradients, lighter accents
    slateBlue: '#4a6fa5', // Slate — tertiary elements, muted accents
    tealPrimary: '#e8364b', // (alias) Primary accent — maps to brand.primary
    tealSecondary: '#f06070', // (alias) Secondary accent — maps to brand.secondary
    neutralGray: '#5c6775', // Neutral — disabled states, muted text
    brown: '#795548', // Disposition legend — transferred call
    teal: '#009688', // Disposition legend — voicemail
    pink: '#e91e63', // Disposition legend — potential new client
  },
  // Functional colors — status indicators only
  status: {
    success: '#16a34a',
    warning: '#f59e0b',
    error: '#ef4444',
    info: '#3b82f6',
  },
  // Background tints
  background: {
    light: '#ffffff',
    tealLight: 'rgba(232, 54, 75, 0.05)', // Red tint — subtle highlights
    tealMedium: 'rgba(232, 54, 75, 0.1)', // Red tint — hover
    tealHover: 'rgba(232, 54, 75, 0.12)', // Red tint — active
    blueLight: 'rgba(30, 58, 95, 0.04)', // Blue tint — subtle info bg
  },
}

// Disposition colors — categorical, distinct hues
export const dispositionColors = {
  Abandoned: '#dc2626',
  'Agent Error': '#b45309',
  'Caller Disconnected': '#a855f7',
  'Current Client': '#16a34a',
  'Hot Lead': '#c72e40',
  'Internal Testing': '#6366f1',
  'No Disposition': '#64748b',
  'Not Relevant': '#64748b',
  'Not Relevant - NCN': '#64748b',
  Other: '#64748b',
  Solicitation: colors.ui.lightBlue,
  'Solicitation - NCN': colors.ui.mediumBlue,
  'Transferred To 3rd Party': '#78716c',
  'Voicemail Processed': '#0d9488',
}

// Chart configurations
export const chartConfig = {
  shadows: 'rgba(0, 0, 0, 0.06) 0px 8px 24px, rgba(0, 0, 0, 0.04) 0px 2px 8px',
  // Gradients — always within the same color family
  gradientColors: {
    calls: [colors.ui.deepBlue, colors.ui.lightBlue], // Blue family
    minutes: [colors.brand.primary, colors.brand.secondary], // Red family
    css: [colors.brand.tertiary, colors.brand.primary], // Red family (dark→bright)
  },
  // Chart series palette — alternates red/blue for visual variety
  colors: [
    colors.brand.primary, // Veta Red
    colors.ui.deepBlue, // Navy
    colors.brand.secondary, // Coral
    colors.ui.lightBlue, // Sky
    colors.brand.tertiary, // Crimson
    colors.ui.slateBlue, // Slate
    colors.status.success, // Green (neutral accent)
    '#6366f1', // Indigo (neutral accent)
    colors.ui.neutralGray, // Gray
    '#eab308', // Gold (neutral accent)
  ],
}

// API endpoints
export const apiEndpoints = {
  metrics: '/api/calls/metrics',
  chartData: '/api/calls/charts/data',
  processReports: '/api/calls/process-reports',
  vendorCallImportPreview: '/api/calls/import/preview',
  vendorCallImportConfirm: '/api/calls/import/confirm',
  planSize: '/api/calls/hubspot/plan-size',
  votes: '/api/calls',
  comments: '/api/comments',
  user: '/api/user',
  userAvailability: '/api/user-availability',
  billingCustomers: '/api/billing/customers',
  billingPortalSession: (id) => `/api/billing/customers/${id}/portal-session`,
  billingCustomerSyncStripe: (id) => `/api/billing/customers/${id}/sync-stripe`,
  billingCustomerContacts: (id) => `/api/billing/customers/${id}/contacts`,
  billingCustomerContact: (id, contactId) => `/api/billing/customers/${id}/contacts/${contactId}`,
  billingCustomerEligibleUsers: (id) => `/api/billing/customers/${id}/eligible-users`,
  billingCustomerStripeInfo: (id) => `/api/billing/customers/${id}/stripe-info`,
  billingCustomerStripeInvoices: (id) => `/api/billing/customers/${id}/stripe-invoices`,
  billingSetup: '/api/billing/setup',
  billingCreateSetupIntent: (id) => `/api/billing/customers/${id}/create-setup-intent`,
  billingLookupRoutingNumber: (rn) => `/api/billing/lookup-routing-number/${rn}`,
  billingPaymentMethods: (id) => `/api/billing/customers/${id}/payment-methods`,
  billingDetachPaymentMethod: (id, pmId) => `/api/billing/customers/${id}/payment-methods/${pmId}`,
  billingCustomerBankDetails: (id) => `/api/billing/customers/${id}/bank-details`,
  billingCustomerPreferredMethod: (id) => `/api/billing/customers/${id}/preferred-payment-method`,
  billingCustomerCountry: (id) => `/api/billing/customers/${id}/country`,
  billingPlans: '/api/billing/plans',
  billingInvoices: '/api/billing/invoices',
  billingInvoice: (id) => `/api/billing/invoices/${id}`,
  billingDashboardStats: '/api/billing/dashboard-stats',
  billingClearPendingInvoices: '/api/billing/clear-pending-invoices',
  billingDeposits: (campaignId) => `/api/billing/deposits/${campaignId}`,
  billingUnlinkedAccounts: '/api/billing/unlinked-accounts',
  billingLinkAccount: (clientAccountId) => `/api/billing/unlinked-accounts/${clientAccountId}/link`,
  billingSearchStripeCustomers: '/api/billing/stripe-customers/search',
  billingResolveStripeSetup: (id) => `/api/billing/customers/${id}/resolve-stripe-setup`,
  billingPendingSetupCount: '/api/billing/pending-setup-count',
  billingSyncStripeCustomers: '/api/billing/sync-stripe-customers',
  adminLogs: '/api/admin/logs',
  adminAuthMigrationSummary: '/api/admin/auth-migration/summary',
  calls: '/api/calls',
  callsCampaigns: '/api/calls/campaigns',
  callsFilters: '/api/calls/filters',
  callsExportCsv: '/api/calls/export-csv',
  callsByCampaign: (encodedName) => `/api/calls/campaign/${encodedName}`,
  // AI processing
  aiProcessCall: '/api/ai/process-call',
  aiProcessCallStream: '/api/ai/process-call-stream',
  openaiAgent: '/api/openai-agent',
  // Per-call comments / QA / sentiment
  commentsByCall: (callId) => `/api/comments/${callId}`,
  qaByCall: (callId) => `/api/qa/${callId}`,
  qaEvaluate: (callId) => `/api/qa/${callId}/evaluate`,
  sentimentByCall: (callId) => `/api/sentiment/${callId}`,
  // Auto QA
  autoQaStatus: '/api/auto-qa/status',
  autoQaFilters: '/api/auto-qa/filters',
  autoQaCampaigns: '/api/auto-qa/campaigns',
  autoQaSelectionConfig: '/api/auto-qa/selection-config',
  autoQaResults: '/api/auto-qa/results',
  autoQaResultsExport: '/api/auto-qa/results/export',
  autoQaBatchDryRun: '/api/auto-qa/batch/dry-run',
  autoQaBatchStop: '/api/auto-qa/batch/stop',
  autoQaBatchHistory: '/api/auto-qa/batch/history',
  autoQaBatchStream: '/api/auto-qa/batch/stream',
  autoQaBatchCancel: (batchId) => `/api/auto-qa/batch/${batchId}/cancel`,
  autoQaBatchReport: (batchId) => `/api/auto-qa/batch/${batchId}/report`,
  autoQaBatchResults: (batchId) => `/api/auto-qa/batch/${batchId}/results`,
  // QA config
  qaConfigCampaigns: '/api/qa-config/campaigns',
  qaConfigCriteria: '/api/qa-config/criteria',
  qaConfigCriteriaTest: '/api/qa-config/criteria/test',
  qaConfigCriterion: (id) => `/api/qa-config/criteria/${id}`,
  qaConfigCriterionHistory: (id) => `/api/qa-config/criteria/${id}/history`,
  qaConfigBaseCriteria: '/api/qa-config/base-criteria',
  qaConfigBaseCriteriaCampaigns: '/api/qa-config/base-criteria/campaigns',
  qaConfigBaseCriteriaTest: '/api/qa-config/base-criteria/test',
  qaConfigBaseCriterion: (criterionKey) => `/api/qa-config/base-criteria/${criterionKey}`,
  qaConfigBaseCriteriaCampaign: (campaignName) => `/api/qa-config/base-criteria/campaign/${encodeURIComponent(campaignName)}`,
  qaConfigGeneratePrompt: '/api/qa-config/generate-prompt',
  qaConfigTemplates: '/api/qa-config/templates',
  qaConfigCalls: '/api/qa-config/calls',
  qaConfigCallsVerify: '/api/qa-config/calls/verify',
  // QA import
  qaImportConfig: '/api/qa/import/config',
  qaImportConfigDetect: '/api/qa/import/config/detect',
  qaImportPreview: '/api/qa/import/preview',
  qaImportConfirm: '/api/qa/import/confirm',
  // Manual QA
  qaManualCampaigns: '/api/qa/manual/campaigns',
  qaManualFormConfig: '/api/qa/manual/form-config',
  qaManualFormConfigItem: (id) => `/api/qa/manual/form-config/${id}`,
  qaManualByCall: (callId) => `/api/qa/manual/${callId}`,
  // Automations
  automations: '/api/automations',
  automationsCampaigns: '/api/automations/campaigns',
  automationsSmsSettings: '/api/automations/sms-settings',
  automation: (id) => `/api/automations/${id}`,
  automationTest: (id) => `/api/automations/${id}/test`,
  automationTestSms: (id) => `/api/automations/${id}/test-sms`,
  automationCampaigns: (id) => `/api/automations/${id}/campaigns`,
  automationCampaign: (id, campaignName) => `/api/automations/${id}/campaigns/${encodeURIComponent(campaignName)}`,
  automationCampaignsBulkToggle: (id) => `/api/automations/${id}/campaigns/bulk-toggle`,
  automationSmsHistory: (id) => `/api/automations/${id}/sms-history`,
  // Email templates
  templates: '/api/templates',
  template: (id) => `/api/templates/${id}`,
  // Campaigns
  campaigns: '/api/campaigns',
  campaignsByName: '/api/campaigns/by-name',
  campaignsSyncData: '/api/campaigns/sync-data',
  campaignsSopUpload: '/api/campaigns/sop/upload',
  campaignsUsageOverview: '/api/campaigns/usage-overview',
  campaign: (campaignId) => `/api/campaigns/${campaignId}`,
  campaignMetrics: (campaignName) => `/api/campaigns/${encodeURIComponent(campaignName)}/metrics`,
  campaignPlanMinutes: (campaignName) => `/api/campaigns/${encodeURIComponent(campaignName)}/plan-minutes`,
  campaignGoogleDocs: (campaign) => `/api/campaigns/google-docs/${encodeURIComponent(campaign)}`,
  campaignSop: (campaign) => `/api/campaigns/sop/${encodeURIComponent(campaign)}`,
  campaignSopContent: (campaign) => `/api/campaigns/sop/${encodeURIComponent(campaign)}/content`,
  // Integrations
  integrationsCampaign: (campaignName) => `/api/integrations/campaign/${encodeURIComponent(campaignName || '')}`,
  // Five9 sync
  five9SyncStart: '/api/five9-sync/start',
  five9SyncStop: '/api/five9-sync/stop',
  five9SyncStats: '/api/five9-sync/stats',
  five9SyncLastSync: '/api/five9-sync/last-sync',
  // Google Sheets
  googleSheetsSyncAllUsers: '/api/google-sheets/sync-all-users',
  // Users
  users: '/api/users',
  // User availability (base userAvailability above)
  userAvailabilityUsers: '/api/user-availability/users',
  userAvailabilityUser: (userID) => `/api/user-availability/${encodeURIComponent(userID)}`,
  userAvailabilityUserById: (userID) => `/api/user-availability/users/${encodeURIComponent(userID)}`,
  userAvailabilityUsersOrg: (campaign) => `/api/user-availability/users-org/${encodeURIComponent(campaign)}`,
  userAvailabilityOperatingHours: (userID) => `/api/user-availability/operating-hours/${encodeURIComponent(userID)}`,
  // Billing — additional endpoints (base billing block above)
  billingStatus: '/api/billing/status',
  billingPendingReview: '/api/billing/pending-review',
  billingPendingReviewItem: (id) => `/api/billing/pending-review/${id}`,
  billingPendingReviewApprove: (id) => `/api/billing/pending-review/${id}/approve`,
  billingPendingReviewByInvoices: '/api/billing/pending-review/by-invoices',
  billingBillableCampaigns: '/api/billing/billable-campaigns',
  billingCsvLinkPreview: '/api/billing/csv-link-preview',
  billingCsvLinkConfirm: '/api/billing/csv-link-confirm',
  billingGenerateMonthly: '/api/billing/generate-monthly',
  billingInvoicesNoRecipient: '/api/billing/invoices/no-recipient',
  billingInvoicesExportCsv: '/api/billing/invoices/export-csv',
  billingInvoiceCancel: (id) => `/api/billing/invoices/${id}/cancel`,
  billingInvoiceSendManualEmail: (id) => `/api/billing/invoices/${id}/send-manual-email`,
  billingCampaignToggleBilling: (campaignId) => `/api/billing/campaigns/${campaignId}/toggle-billing`,
  billingExportBankDetails: '/api/billing/export-bank-details',
  billingExportsManualAch: '/api/billing/exports/manual-ach',
  billingDraftInvoicesReuploadPreview: '/api/billing/draft-invoices/reupload-preview',
  billingDraftInvoicesReuploadCommitStream: '/api/billing/draft-invoices/reupload-commit-stream',
  billingSyncHubspotPlansStream: '/api/billing/sync-hubspot-plans-stream',
  billingSyncHubspotPlansPrepareStream: '/api/billing/sync-hubspot-plans-prepare-stream',
  billingSyncHubspotPlansCommitStream: '/api/billing/sync-hubspot-plans-commit-stream',
}

// Date format patterns
export const dateFormats = {
  display: {
    date: 'MMMM dd, yyyy',
    dateTime: 'MMMM dd, yyyy hh:mm a',
    shortDate: 'MM/dd/yyyy',
  },
  timezone: 'America/New_York',
}

// Payment method options (filtered by country_code on billing_customers)
export const PAYMENT_METHODS = {
  credit_card: { label: 'Credit Card', countries: ['CA', 'US'] },
  pad: { label: 'Pre-Authorized Debit (PAD)', countries: ['CA'] },
  ach: { label: 'ACH Bank Transfer', countries: ['US'] },
}

// US states + DC + PR — shared by ACH form + admin views
export const US_STATES = [
  ['AL', 'Alabama'],
  ['AK', 'Alaska'],
  ['AZ', 'Arizona'],
  ['AR', 'Arkansas'],
  ['CA', 'California'],
  ['CO', 'Colorado'],
  ['CT', 'Connecticut'],
  ['DE', 'Delaware'],
  ['FL', 'Florida'],
  ['GA', 'Georgia'],
  ['HI', 'Hawaii'],
  ['ID', 'Idaho'],
  ['IL', 'Illinois'],
  ['IN', 'Indiana'],
  ['IA', 'Iowa'],
  ['KS', 'Kansas'],
  ['KY', 'Kentucky'],
  ['LA', 'Louisiana'],
  ['ME', 'Maine'],
  ['MD', 'Maryland'],
  ['MA', 'Massachusetts'],
  ['MI', 'Michigan'],
  ['MN', 'Minnesota'],
  ['MS', 'Mississippi'],
  ['MO', 'Missouri'],
  ['MT', 'Montana'],
  ['NE', 'Nebraska'],
  ['NV', 'Nevada'],
  ['NH', 'New Hampshire'],
  ['NJ', 'New Jersey'],
  ['NM', 'New Mexico'],
  ['NY', 'New York'],
  ['NC', 'North Carolina'],
  ['ND', 'North Dakota'],
  ['OH', 'Ohio'],
  ['OK', 'Oklahoma'],
  ['OR', 'Oregon'],
  ['PA', 'Pennsylvania'],
  ['RI', 'Rhode Island'],
  ['SC', 'South Carolina'],
  ['SD', 'South Dakota'],
  ['TN', 'Tennessee'],
  ['TX', 'Texas'],
  ['UT', 'Utah'],
  ['VT', 'Vermont'],
  ['VA', 'Virginia'],
  ['WA', 'Washington'],
  ['WV', 'West Virginia'],
  ['WI', 'Wisconsin'],
  ['WY', 'Wyoming'],
  ['DC', 'District of Columbia'],
  ['PR', 'Puerto Rico'],
]

// Animation presets
export const animations = {
  easings: {
    default: 'cubic-bezier(0.22, 1, 0.36, 1)',
    smooth: 'power3.out',
    spring: 'elastic.out(1, 0.3)',
  },
  durations: {
    short: 0.2,
    medium: 0.4,
    long: 0.8,
  },
}
